// src/lib/coverage/buildCoverageInputs.ts
// ============================================================
// FILE -> EVIDENCE. The bridge between what the presenter drops and what the panel reads.
//
// Two kinds of dropped file produce evidence, and they produce it differently:
//
//   PATH A DOCUMENTS  a known strategy PDF, matched by filename against
//                     pathADocumentBindings. The binding IS the evidence; nothing is read
//                     out of the file, and the binding table's header says so plainly.
//
//   WORKBOOKS         D1 / D2 / D3, parsed by parseInventory and scored by deriveScores.
//                     The evidence is computed from the cells. This is the half that makes
//                     an upload produce a score change rather than a scripted one.
//
// Anything else is accepted as a document and produces no evidence. That is deliberate:
// an unrecognised file must not quietly claim coverage it cannot support.
//
// COVERS-CATEGORIES, and why only D2 declares one:
//   A source declares a category "covered" when its SILENCE about a measure is meaningful.
//   A board priorities workbook is meant to list every board priority, so a theme with no
//   row is a real finding and measure 6 is genuinely assumed. A staffing inventory is not
//   attempting to speak to change management or partnerships at all, so its silence there
//   is out of scope rather than a finding, and claiming otherwise would manufacture gaps.
//   D3 scores all seven of its category's measures, so it has no silence to declare.
//
// Mock layer. Pure apart from reading the File the caller already has.
// ============================================================

import { bindingForFileName } from '../../data/coverage/pathADocumentBindings';
import type { CoverageCategory } from '../../data/coverage/measureGroupMap';
import type { InstrumentId } from '../../data/coverage/gapInstruments';
import { OPENING_COVERAGE_STATE } from '../../data/coverage/openingCoverageState';
import {
  evidenceSourceFromScores,
  type CoverageInputs,
  type EvidenceSource,
} from './computeCoverage';
import { deriveScores, type OpportunityRequirement } from './deriveScores';
import { parseWorkbook, type InventoryTemplate } from './parseInventory';
import * as XLSX from 'xlsx';

/** Display label per template. Rendered verbatim after the source-kind prefix. */
export const TEMPLATE_LABELS: Readonly<Record<InventoryTemplate, string>> = {
  staffing: 'Staffing and Capacity Inventory',
  boardPriorities: 'Board Priorities Extract',
  dataAssets: 'Data Asset and Platform Inventory',
  governanceCharter: 'Governance and Compliance Charter',
};

/**
 * See the COVERS-CATEGORIES note in the header. D2 and D4 have meaningful silence.
 *
 * A charter that enumerates its control areas and omits one has said something about the
 * omission, exactly as a board priorities sheet that lists no Market Position priority
 * has. D4 reaches every measure in Compliance and Risk, so the category declaration adds
 * nothing today; it is here so that a charter submitted with an area MISSING still marks
 * that measure assumed rather than leaving it silently on default.
 */
const TEMPLATE_COVERS: Readonly<Partial<Record<InventoryTemplate, CoverageCategory[]>>> = {
  boardPriorities: ['Strategic Value'],
  governanceCharter: ['Compliance & Risk'],
};

/**
 * The opportunity the per-opportunity measures (43, 51) are scored against.
 *
 * Care Coordination is the demo's rank-1 use case. Held here rather than in the staffing
 * formulas because it is a property of the demo, not of the D1 template.
 */
export const DEMO_OPPORTUNITY_REQUIREMENT: OpportunityRequirement = {
  opportunityId: 'care-coordination',
  label: 'Care Coordination and Transition Management',
  requiredSkillAreas: ['data engineering', 'integration', 'clinical workflow', 'reporting'],
  requiredFte: 3.0,
};

/**
 * Which named instrument an evidence source came from, or null.
 *
 * A NAMED INSTRUMENT IS A MATCHED TEMPLATE, NOT A MATCHED FILENAME. Path A documents bind
 * measures by their filename against pathADocumentBindings; nothing is read out of them.
 * The instrument count on the intake card exists to tell a client which of their uploads
 * was actually PARSED, so a filename match must not inflate it.
 *
 * Keyed on the source id, which evidenceFromWorkbookBytes builds as `workbook-<template>`
 * and which is already persisted on the upload record.
 */
export const TEMPLATE_TO_INSTRUMENT: Readonly<
  Record<InventoryTemplate, Exclude<InstrumentId, 'pathA'>>
> = {
  staffing: 'D1',
  boardPriorities: 'D2',
  dataAssets: 'D3',
  governanceCharter: 'D4',
};

const WORKBOOK_ID_PREFIX = 'workbook-';

export function namedInstrumentOf(source: EvidenceSource): InstrumentId | null {
  if (!source.id.startsWith(WORKBOOK_ID_PREFIX)) return null;
  const template = source.id.slice(WORKBOOK_ID_PREFIX.length) as InventoryTemplate;
  return TEMPLATE_TO_INSTRUMENT[template] ?? null;
}

/** How many DISTINCT named instruments a set of uploads matched. */
export function matchedInstrumentCount(sources: EvidenceSource[]): number {
  return new Set(sources.map(namedInstrumentOf).filter(Boolean)).size;
}

/** How many named instruments exist to match. Derived, never typed. */
export const NAMED_INSTRUMENT_COUNT = Object.keys(TEMPLATE_TO_INSTRUMENT).length;

/** Turn already-read workbook bytes into an evidence source, or null. Never throws. */
export function evidenceFromWorkbookBytes(buffer: ArrayBuffer): EvidenceSource | null {
  try {
    const parsed = parseWorkbook(XLSX.read(buffer, { type: 'array' }));
    if (!parsed.ok) return null;

    const derived =
      parsed.template === 'staffing'
        ? deriveScores({
            template: 'staffing',
            rows: parsed.rows,
            opportunities: [DEMO_OPPORTUNITY_REQUIREMENT],
          })
        : deriveScores(parsed);

    return evidenceSourceFromScores(
      {
        id: `${WORKBOOK_ID_PREFIX}${parsed.template}`,
        label: TEMPLATE_LABELS[parsed.template],
        // 'Internal document', not 'Structured inventory': the label already names the
        // file, so the prefix only has to say whose document it is. One prefix across all
        // client-supplied evidence keeps the attribution line uniform.
        kind: 'Internal document',
        coversCategories: TEMPLATE_COVERS[parsed.template],
      },
      derived,
    );
  } catch {
    return null;
  }
}

/**
 * Resolve a dropped file to an evidence source, or null when it produces none.
 * Never throws: a file that cannot be read is simply a document with no evidence.
 */
export async function evidenceForFile(file: File): Promise<EvidenceSource | null> {
  const pathA = bindingForFileName(file.name);
  if (pathA) {
    return {
      id: pathA.fileName,
      label: pathA.label,
      kind: 'Internal document',
      measureIds: [...pathA.measureIds],
    };
  }

  const name = (file.name || '').toLowerCase();
  if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) return null;

  try {
    return evidenceFromWorkbookBytes(await file.arrayBuffer());
  } catch {
    return null;
  }
}

/**
 * Assemble the reducer's inputs.
 *
 * Surveys and profile come from the opening state, which is where the demo's completed
 * modules and populated fields are declared. Evidence comes entirely from what has been
 * uploaded, so an account that has dropped nothing has no evidence, by construction rather
 * than by a seeded exception.
 */
export function buildCoverageInputs(evidenceSources: EvidenceSource[] = []): CoverageInputs {
  return {
    completedSurveyModules: [...OPENING_COVERAGE_STATE.completedSurveyModules],
    populatedProfileFields: [...OPENING_COVERAGE_STATE.populatedProfileFields],
    // Later uploads of the same instrument replace earlier ones rather than double-counting.
    evidenceSources: dedupeById(evidenceSources),
  };
}

// ---- what to tell the presenter about a dropped file -----------------------

export interface UploadOutcome {
  /** True when the file produced measure evidence. */
  applied: boolean;
  /** One honest sentence about what the file did, or did not, change. */
  note: string;
}

/**
 * What a dropped file actually did.
 *
 * EITHER A FILE APPLIES FULLY OR IT APPLIES NOTHING. evidenceForFile returns a complete
 * source or null, and the store writes one field, so there is no partial state to report
 * and no half-applied workbook to explain.
 *
 * The failure notes matter more than the success ones. A workbook that could not be read
 * must SAY so: it still uploads as a document, and a presenter who is not told will stand
 * in front of a panel that did not move and improvise a reason.
 */
export function uploadOutcome(fileName: string, evidence: EvidenceSource | null): UploadOutcome {
  const name = (fileName || '').toLowerCase();
  const isWorkbook = name.endsWith('.xlsx') || name.endsWith('.xls');

  if (evidence) {
    const count = evidence.measureIds.length;
    // Verb agrees with the count. A blank template legitimately produces one measure, so
    // the singular case is reachable in the product, not only in a test.
    const measures = count === 1 ? '1 measure now comes' : `${count} measures now come`;
    return {
      applied: true,
      note: `Read as ${evidence.label}. ${measures} from this file.`,
    };
  }

  if (isWorkbook) {
    return {
      applied: false,
      note:
        'This workbook does not match one of the three templates, so no measure changed. ' +
        'It is kept with your documents. Download a template from the cards above to have it read.',
    };
  }

  return {
    applied: false,
    note:
      'Kept with your documents as context. It does not match a named template, so no ' +
      'measure changed.',
  };
}

function dedupeById(sources: EvidenceSource[]): EvidenceSource[] {
  const byId = new Map<string, EvidenceSource>();
  sources.forEach((s) => byId.set(s.id, s));
  return [...byId.values()];
}
